'use client';

import type React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import { useBandcampItems } from '../../hooks';

const Section = styled.section`
  min-height: 80vh;
  background: var(--bg-tertiary, #0d0d18);
  padding: 10vh 0 12vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 10;
  transition: background-color 0.3s ease;

  @media (max-width: 768px) {
    min-height: auto;
    padding: 8vh 0 9vh;
  }

  @media (max-width: 480px) {
    padding: 7vh 0 8vh;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 6vh;
  padding: 0 16px;
`;

const SectionLabel = styled.p`
  font-size: 0.85rem;
  color: #ff2d95;
  font-variant-caps: all-petite-caps;
  letter-spacing: 4px;
  margin: 0 0 8px;
  font-weight: 600;

  @media (max-width: 480px) {
    letter-spacing: 2.5px;
    font-size: 0.78rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3.2rem);
  color: var(--text-primary, #fff);
  font-variant-caps: all-petite-caps;
  margin: 0 0 12px;
  font-weight: 400;

  @media (max-width: 480px) {
    font-size: clamp(1.6rem, 8vw, 2.2rem);
    margin: 0 0 10px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 0.95rem;
  color: var(--text-secondary, rgba(255, 255, 255, 0.75));
  font-variant-caps: all-petite-caps;
  margin: 0;
  max-width: 480px;

  @media (max-width: 480px) {
    font-size: 0.86rem;
    max-width: 340px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
  gap: 24px;
  width: 100%;
  max-width: 1100px;
  padding: 0 20px;

  @media (max-width: 768px) {
    gap: 16px;
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const Card = styled.div`
  border-radius: 12px;
  background: var(--card-bg, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--card-border, rgba(255, 255, 255, 0.08));
  padding: 24px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0 4px 18px var(--card-shadow, rgba(0, 0, 0, 0.2));
  transition: all 0.3s ease;

  @media (max-width: 480px) {
    padding: 16px;
  }

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(0, 255, 0, 0.6);
    box-shadow: 0 8px 30px var(--card-shadow, rgba(0, 0, 0, 0.4));
  }
`;

const CardType = styled.span`
  font-size: 0.68rem;
  color: #00ff00;
  font-variant-caps: all-petite-caps;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 8px;
  font-weight: 700;
`;

const CardTitle = styled.h3`
  font-size: 1.2rem;
  color: var(--text-primary, #fff);
  font-variant-caps: all-petite-caps;
  margin: 0 0 16px;
  font-weight: 600;
`;

const BandcampLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 20px;
  background: #00ff00;
  color: #000000;
  text-decoration: none;
  font-size: 0.8rem;
  font-variant-caps: all-petite-caps;
  font-weight: 700;
  letter-spacing: 1px;
  transition: all 0.2s ease;

  &:hover {
    background: #ff2d95;
    color: #ffffff;
    transform: scale(1.02);
  }
`;

/**
 * Bandcamp releases & discography showcase section with localized text.
 */
export const BandcampSection: React.FC = () => {
  const { items, loading } = useBandcampItems();
  const { t } = useLanguage();

  return (
    <Section id="bandcamp-releases" aria-label="Bandcamp Releases">
      <SectionHeader>
        <SectionLabel>{t('bcSectionLabel')}</SectionLabel>
        <SectionTitle>{t('bcSectionTitle')}</SectionTitle>
        <SectionSubtitle>{t('bcSectionSubtitle')}</SectionSubtitle>
      </SectionHeader>

      <Grid>
        {items.map((item) => (
          <Card key={`bc-${item.id}`}>
            <div>
              <CardType>{item.type}</CardType>
              <CardTitle>{item.title}</CardTitle>
            </div>
            <BandcampLink
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Buy or stream ${item.title} on Bandcamp`}
            >
              {t('bcCtaButton')}
            </BandcampLink>
          </Card>
        ))}
      </Grid>
      {loading && <p style={{ color: 'rgba(255,255,255,0.4)', marginTop: 20 }}>{t('bcLoading')}</p>}
    </Section>
  );
};

export default BandcampSection;
